/**
 * @fileoverview Slice Redux para gerenciamento de favoritos com sincronização remota.
 * Gerencia estado dos favoritos local e sincronização com backend via sessão.
 * 
 * @module FavoritesSlice
 * @since 1.0.0
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api.js';
import { mobilePersistence } from '../../utils/mobilePersistence.js';
import { mobileConfig } from '../../utils/mobileUtils.js';

/**
 * Thunk para carregar favoritos da sessão ativa.
 * @async
 * @function loadFavoritesAsync
 * @returns {Promise<Array>} Dados dos favoritos
 */
export const loadFavoritesAsync = createAsyncThunk(
  'favorites/loadFavorites',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Verificar se usuário está autenticado
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        console.log('🚫 [FAVORITES] Usuário não autenticado, pulando carregamento dos favoritos');
        return [];
      }
      
      const response = await api.get('/users/session/favorites', {
        timeout: mobileConfig.requestTimeout
      });
      const serverData = response.data?.data?.favorites || response.data?.favorites || [];
      
      // Salva dados do servidor localmente (apenas em mobile)
      mobilePersistence.saveFavorites(serverData);
      return serverData;
    } catch (error) {
      // Se favoritos não encontrados ou erro de rede, usa dados locais
      if (error.response?.status === 404 || error.response?.status === 501 || error.code === 'NETWORK_ERROR') {
        console.warn('Favorites endpoint not available, using local storage');
        return mobilePersistence.loadFavorites();
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar favoritos');
    }
  }
);

/**
 * Thunk para salvar favoritos no servidor (método legado).
 * @async
 * @function saveFavoritesAsync
 * @param {Array} favoritesData - Dados dos favoritos
 * @returns {Promise<void>}
 */
export const saveFavoritesAsync = createAsyncThunk(
  'favorites/saveFavorites',
  async (favoritesData, { rejectWithValue }) => {
    try {
      await api.post('/users/session/save-favorites', { favoritesData });
      return favoritesData;
    } catch (error) {
      // Se endpoint não implementado, apenas avisa mas não falha
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.warn('Save favorites endpoint not available, favorites saved locally only');
        return favoritesData;
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao salvar favoritos');
    }
  }
);

/**
 * Thunk para processar ação atômica nos favoritos.
 * @async
 * @function processFavoriteActionAsync
 * @param {Object} payload - Dados da ação
 * @param {string} payload.action - Ação: 'insert' ou 'delete'
 * @param {Array} payload.favoritesData - Dados dos favoritos
 * @returns {Promise<Object>}
 */
export const processFavoriteActionAsync = createAsyncThunk(
  'favorites/processFavoriteAction',
  async ({ action, favoritesData }, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/session/save-favorites', { action, favoritesData });
      return { action, favoritesData, response: response.data };
    } catch (error) {
      // Se endpoint não implementado, apenas avisa mas não falha
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.warn('Process favorite action endpoint not available, using local only');
        return { action, favoritesData };
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao processar ação nos favoritos');
    }
  }
);

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {
    items: [],
    isLoading: false,
    error: null,
    lastSync: null,
    needsSync: false
  },
  reducers: {
    toggleFavorite: (state, action) => {
      const product = action.payload;
      const favoriteProduct = {
        productId: product.id,
        name: product.nome,
        brand: product.marca,
        image: product.imagem,
        category: product.categoria
      };
      const existingIndex = state.items.findIndex(fav => fav.productId === product.id);
      if (existingIndex >= 0) {
        state.items.splice(existingIndex, 1);
      } else {
        state.items.push(favoriteProduct);
      }
      // Marca que precisa sincronizar
      state.needsSync = true;
      // Salva imediatamente no localStorage (apenas em mobile)
      mobilePersistence.saveFavorites(state.items);
    },
    clearFavorites: (state) => {
      state.items = [];
      state.needsSync = false; // Não sincronizar quando limpar localmente
    },
    clearFavoritesWithSync: (state) => {
      state.items = [];
      state.needsSync = true; // Sincronizar quando explicitamente solicitado
    },
    clearError: (state) => {
      state.error = null;
    },
    loadFromLocalStorage: (state) => {
      const localData = mobilePersistence.loadFavorites();
      if (localData.length > 0) {
        state.items = localData;
        state.needsSync = true; // Marca para sincronizar com servidor quando possível
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Load favorites
      .addCase(loadFavoritesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadFavoritesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.lastSync = new Date().toISOString();
        state.needsSync = false; // Dados carregados do servidor não precisam sync
      })
      .addCase(loadFavoritesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save favorites
      .addCase(saveFavoritesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveFavoritesAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.lastSync = new Date().toISOString();
        state.needsSync = false;
      })
      .addCase(saveFavoritesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Process favorite action
      .addCase(processFavoriteActionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(processFavoriteActionAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.lastSync = new Date().toISOString();
        state.needsSync = false;
      })
      .addCase(processFavoriteActionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { toggleFavorite, clearFavorites, clearFavoritesWithSync, clearError, loadFromLocalStorage } = favoritesSlice.actions;
export default favoritesSlice.reducer;